import {
  App,
  Button,
  Card,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import { DeleteOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { hrDepthApi, type SalaryStructure } from '@/api/hr-depth';
import { hrApi } from '@/api/hr';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

const PKR = (n: number | string | null | undefined) =>
  n == null ? '—' : Number(n).toLocaleString('en-PK', { maximumFractionDigits: 2 });

interface ComponentRow {
  componentId: number;
  amount: number;
  isPercentage: boolean;
}

interface FormShape {
  effectiveFrom: Dayjs;
  components: ComponentRow[];
  revisionReason?: string;
}

export function SalaryStructurePage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canEdit = useAuthStore((s) => s.hasAuthority('HR_MGMT:EDIT'));

  const [staffId, setStaffId] = useState<number | undefined>(undefined);
  const [editing, setEditing] = useState(false);
  const [form] = Form.useForm<FormShape>();

  const { data: staffPage } = useQuery({
    queryKey: ['hr', 'staff', 'active', 'all'],
    queryFn: () => hrApi.searchStaff({ active: true, size: 500 }),
    staleTime: 60_000,
  });
  const staff = staffPage?.rows ?? [];

  const { data: components } = useQuery({
    queryKey: ['hr', 'salary-components', 'active'],
    queryFn: () => hrDepthApi.listSalaryComponents(true),
    staleTime: 60_000,
  });

  const { data: current } = useQuery({
    queryKey: ['hr', 'salary-structures', staffId, 'current'],
    queryFn: () => hrDepthApi.currentStructure(staffId!),
    enabled: staffId != null,
  });

  const { data: history } = useQuery({
    queryKey: ['hr', 'salary-structures', staffId, 'history'],
    queryFn: () => hrDepthApi.structureHistory(staffId!),
    enabled: staffId != null,
  });

  const set = useMutation({
    mutationFn: (v: FormShape) =>
      hrDepthApi.setStructure(staffId!, {
        effectiveFrom: v.effectiveFrom.format('YYYY-MM-DD'),
        components: v.components.map((c) => ({
          componentId: c.componentId,
          amount: Number(c.amount).toFixed(2),
          isPercentage: c.isPercentage,
        })),
        revisionReason: v.revisionReason?.trim() || undefined,
      }),
    onSuccess: () => {
      message.success('Salary structure updated');
      qc.invalidateQueries({ queryKey: ['hr', 'salary-structures'] });
      setEditing(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const startEdit = () => {
    form.resetFields();
    if (current) {
      form.setFieldsValue({
        effectiveFrom: dayjs(current.effectiveFrom).add(1, 'month').startOf('month'),
        components: current.components.map((c) => ({
          componentId: c.componentId,
          amount: Number(c.amount),
          isPercentage: c.isPercentage,
        })),
      });
    } else {
      form.setFieldsValue({
        effectiveFrom: dayjs().startOf('month'),
        components: [{ componentId: components?.find((c) => c.name === 'Basic Salary')?.id ?? components?.[0]?.id ?? 0, amount: 0, isPercentage: false }],
      });
    }
    setEditing(true);
  };

  // Live preview of computed gross from form components
  const watchedComponents = Form.useWatch('components', form) as ComponentRow[] | undefined;
  const computedGross = (() => {
    if (!watchedComponents) return null;
    const componentMap = new Map(components?.map((c) => [c.id, c]) ?? []);
    const basic = watchedComponents.find((row) => {
      const def = row && componentMap.get(row.componentId);
      return def?.name === 'Basic Salary' && row && !row.isPercentage;
    })?.amount ?? 0;
    let earnings = 0;
    let deductions = 0;
    for (const row of watchedComponents) {
      if (!row || row.componentId == null || row.amount == null) continue;
      const def = componentMap.get(row.componentId);
      if (!def) continue;
      const value = row.isPercentage ? (Number(basic) * Number(row.amount)) / 100 : Number(row.amount);
      if (def.kind === 'EARNING') earnings += value;
      else deductions += value;
    }
    return { earnings, deductions, gross: earnings - deductions };
  })();

  const columns: TableProps<SalaryStructure>['columns'] = [
    {
      title: 'Effective from',
      dataIndex: 'effectiveFrom',
      width: 140,
      render: (d: string) => dayjs(d).format('D MMM YYYY'),
    },
    {
      title: 'Effective to',
      dataIndex: 'effectiveTo',
      width: 140,
      render: (d: string | null) => d ? dayjs(d).format('D MMM YYYY') : <Tag color="success">Active</Tag>,
    },
    {
      title: 'Earnings',
      dataIndex: 'totalEarnings',
      width: 130,
      align: 'right',
      render: (v: string) => `PKR ${PKR(v)}`,
    },
    {
      title: 'Deductions',
      dataIndex: 'totalDeductions',
      width: 130,
      align: 'right',
      render: (v: string) => `PKR ${PKR(v)}`,
    },
    {
      title: 'Gross',
      dataIndex: 'grossSalary',
      width: 140,
      align: 'right',
      render: (v: string) => <Typography.Text strong>PKR {PKR(v)}</Typography.Text>,
    },
    {
      title: 'Reason',
      dataIndex: 'revisionReason',
      render: (r: string | null) => r ?? '—',
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / HR"
        title="Salary structures"
        description="Per-staff salary breakdown. Setting a new structure closes the previous one (history is preserved)."
        actions={
          canEdit && staffId != null ? (
            <Button type="primary" icon={<SaveOutlined />} onClick={startEdit}>
              {current ? 'Revise structure' : 'Set initial structure'}
            </Button>
          ) : null
        }
      />

      <Space wrap size={12}>
        <Select
          showSearch
          optionFilterProp="label"
          placeholder="Pick a staff member"
          style={{ minWidth: 320 }}
          value={staffId}
          onChange={(v) => {
            setStaffId(v);
            setEditing(false);
          }}
          options={staff.map((s) => ({ value: s.id, label: `${s.firstName} ${s.lastName}${s.designationName ? ` · ${s.designationName}` : ''}` }))}
        />
      </Space>

      {staffId == null ? (
        <Empty description="Pick a staff member to view their salary structure" />
      ) : editing ? (
        <Card>
          <Form form={form} layout="vertical" onFinish={(v) => set.mutate(v)}>
            <Form.Item label="Effective from" name="effectiveFrom" rules={[{ required: true }]}>
              <DatePicker style={{ width: 220 }} format="DD MMM YYYY" />
            </Form.Item>

            <Form.List name="components">
              {(fields, { add, remove }) => (
                <Space orientation="vertical" style={{ width: '100%' }} size={6}>
                  {fields.map((field) => (
                    <Space key={field.key} align="baseline" size={6} wrap>
                      <Form.Item
                        {...field}
                        name={[field.name, 'componentId']}
                        rules={[{ required: true }]}
                        style={{ marginBottom: 0, minWidth: 240 }}
                      >
                        <Select
                          showSearch
                          optionFilterProp="label"
                          placeholder="Component"
                          options={(components ?? []).map((c) => ({
                            value: c.id,
                            label: `${c.name} · ${c.kind}`,
                          }))}
                        />
                      </Form.Item>
                      <Form.Item
                        {...field}
                        name={[field.name, 'amount']}
                        rules={[{ required: true, type: 'number', min: 0 }]}
                        style={{ marginBottom: 0 }}
                      >
                        <InputNumber min={0} step={500} style={{ width: 140 }} addonAfter="PKR / %" />
                      </Form.Item>
                      <Form.Item
                        {...field}
                        name={[field.name, 'isPercentage']}
                        valuePropName="checked"
                        style={{ marginBottom: 0 }}
                      >
                        <Switch checkedChildren="%" unCheckedChildren="₨" />
                      </Form.Item>
                      <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => remove(field.name)} />
                    </Space>
                  ))}
                  <Button onClick={() => add({ componentId: components?.[0]?.id, amount: 0, isPercentage: false })} icon={<PlusOutlined />}>
                    Add component
                  </Button>
                </Space>
              )}
            </Form.List>

            {computedGross && (
              <Space wrap size={[14, 4]} style={{ marginTop: 16 }}>
                <Tag>Earnings: PKR {PKR(computedGross.earnings)}</Tag>
                <Tag color="warning">Deductions: PKR {PKR(computedGross.deductions)}</Tag>
                <Tag color="processing" style={{ fontSize: 13 }}>Gross: PKR {PKR(computedGross.gross)}</Tag>
              </Space>
            )}

            <Form.Item label="Revision reason" name="revisionReason" style={{ marginTop: 16 }} rules={[{ max: 1000 }]}>
              <Input.TextArea autoSize={{ minRows: 1, maxRows: 3 }} placeholder="Why this revision? (optional)" />
            </Form.Item>

            <Space>
              <Button type="primary" loading={set.isPending} onClick={() => form.submit()} icon={<SaveOutlined />}>
                Save
              </Button>
              <Button onClick={() => setEditing(false)}>Cancel</Button>
            </Space>
          </Form>
        </Card>
      ) : (
        <>
          {current ? (
            <Card>
              <Space orientation="vertical" style={{ width: '100%' }} size={10}>
                <Space wrap size={[14, 4]}>
                  <Typography.Text strong>Active structure</Typography.Text>
                  <Tag color="success">From {dayjs(current.effectiveFrom).format('D MMM YYYY')}</Tag>
                  <Tag color="processing" style={{ fontSize: 13 }}>
                    Gross PKR {PKR(current.grossSalary)}
                  </Tag>
                </Space>
                <Table
                  rowKey="componentId"
                  size="small"
                  pagination={false}
                  dataSource={current.components}
                  columns={[
                    { title: 'Component', dataIndex: 'componentName' },
                    {
                      title: 'Kind',
                      dataIndex: 'kind',
                      width: 120,
                      render: (k: string) => <Tag color={k === 'EARNING' ? 'success' : 'warning'}>{k}</Tag>,
                    },
                    {
                      title: 'Amount',
                      width: 160,
                      align: 'right',
                      render: (_, c) =>
                        c.isPercentage ? `${Number(c.amount).toFixed(2)} %` : `PKR ${PKR(c.amount)}`,
                    },
                  ]}
                />
              </Space>
            </Card>
          ) : (
            <Empty description="No salary structure set yet" />
          )}
          {history && history.length > 0 && (
            <Card title="Revision history" styles={{ body: { padding: 0 } }}>
              <Table
                rowKey="id"
                size="small"
                pagination={false}
                dataSource={history}
                columns={columns}
              />
            </Card>
          )}
        </>
      )}
    </div>
  );
}
